/**
 * PasswordChangeForm Component
 * คอมโพเนนต์ฟอร์มเปลี่ยนรหัสผ่าน
 */

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { authApi } from '../auth.api'
import { PASSWORD_RULES, getPasswordStrength } from '../password.validation'
import { Loader2, CheckCircle, Eye, EyeOff, Check, X } from 'lucide-react'

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(8, 'Minimum 8 characters')
      .regex(/[A-Z]/, 'At least 1 uppercase letter')
      .regex(/[a-z]/, 'At least 1 lowercase letter')
      .regex(/[0-9]/, 'At least 1 number')
      .regex(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, 'At least 1 special character'),
    confirmPassword: z.string().min(1, 'Confirm password is required'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type PasswordFormValues = z.infer<typeof passwordSchema>

export function PasswordChangeForm() {
  const { t, i18n } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const isThaiLang = i18n.language === 'th'

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const newPassword = watch('newPassword')
  const strength = getPasswordStrength(newPassword)

  const onSubmit = async (values: PasswordFormValues) => {
    setIsLoading(true)
    setError(null)
    setSuccess(false)
    try {
      await authApi.changePassword(values)
      setSuccess(true)
      reset()
      setTimeout(() => {
        setSuccess(false)
      }, 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password')
    } finally {
      setIsLoading(false)
    }
  }

  const getStrengthColor = () => {
    if (strength < 40) return 'bg-red-500'
    if (strength < 60) return 'bg-orange-500'
    if (strength < 80) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  const getStrengthLabel = () => {
    if (strength < 40) return t('auth.passwordWeak')
    if (strength < 60) return t('auth.passwordFair')
    if (strength < 80) return t('auth.passwordGood')
    return t('auth.passwordStrong')
  }

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e)
      }}
      className="max-w-md space-y-5"
    >
      {success && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700 dark:bg-green-950/30 dark:text-green-400">
          <CheckCircle className="h-4 w-4" />
          {t('auth.passwordChanged')}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Current Password */}
      <div className="space-y-2">
        <label htmlFor="current-password" className="text-sm font-medium">
          {t('auth.currentPassword')}
        </label>
        <div className="relative">
          <input
            id="current-password"
            type={showCurrentPassword ? 'text' : 'password'}
            className={`bg-background focus:ring-primary/30 h-10 w-full rounded-lg border px-3 pr-10 text-sm focus:ring-2 focus:outline-none ${
              errors.currentPassword ? 'border-red-500' : 'border-input'
            }`}
            {...register('currentPassword')}
          />
          <button
            type="button"
            onClick={() => {
              setShowCurrentPassword(!showCurrentPassword)
            }}
            className="text-muted-foreground absolute top-1/2 right-3 -translate-y-1/2"
            tabIndex={-1}
          >
            {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.currentPassword && (
          <p className="text-xs text-red-500">{errors.currentPassword.message}</p>
        )}
      </div>

      {/* New Password */}
      <div className="space-y-2">
        <label htmlFor="new-password" className="text-sm font-medium">
          {t('auth.newPassword')}
        </label>
        <div className="relative">
          <input
            id="new-password"
            type={showNewPassword ? 'text' : 'password'}
            className={`bg-background focus:ring-primary/30 h-10 w-full rounded-lg border px-3 pr-10 text-sm focus:ring-2 focus:outline-none ${
              errors.newPassword ? 'border-red-500' : 'border-input'
            }`}
            {...register('newPassword')}
          />
          <button
            type="button"
            onClick={() => {
              setShowNewPassword(!showNewPassword)
            }}
            className="text-muted-foreground absolute top-1/2 right-3 -translate-y-1/2"
            tabIndex={-1}
          >
            {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.newPassword && <p className="text-xs text-red-500">{errors.newPassword.message}</p>}
      </div>

      {/* Password Strength */}
      {newPassword && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{t('auth.passwordStrength')}</span>
            <span className="font-medium">{getStrengthLabel()}</span>
          </div>
          <div className="bg-muted h-1.5 w-full overflow-hidden rounded-full">
            <div
              className={`h-full transition-all duration-300 ${getStrengthColor()}`}
              style={{ width: `${String(strength)}%` }}
            />
          </div>

          {/* Password Rules */}
          <div className="space-y-1">
            {PASSWORD_RULES.map((rule) => {
              const passed = rule.test(newPassword)
              return (
                <div key={rule.id} className="flex items-center gap-2 text-xs">
                  {passed ? (
                    <Check className="h-3 w-3 text-green-500" />
                  ) : (
                    <X className="text-muted-foreground h-3 w-3" />
                  )}
                  <span
                    className={
                      passed ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
                    }
                  >
                    {isThaiLang ? rule.labelTh : rule.labelEn}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Confirm Password */}
      <div className="space-y-2">
        <label htmlFor="confirm-password" className="text-sm font-medium">
          {t('auth.confirmPassword')}
        </label>
        <input
          id="confirm-password"
          type="password"
          className={`bg-background focus:ring-primary/30 h-10 w-full rounded-lg border px-3 text-sm focus:ring-2 focus:outline-none ${
            errors.confirmPassword ? 'border-red-500' : 'border-input'
          }`}
          {...register('confirmPassword')}
        />
        {errors.confirmPassword && (
          <p className="text-xs text-red-500">{errors.confirmPassword.message}</p>
        )}
      </div>

      <Button type="submit" disabled={isLoading}>
        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t('auth.changePassword')}
      </Button>
    </form>
  )
}
